import { create, el } from "@dhx/ts-common/dom";
import { eventHandler, locate } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { IField, IFilterMeta } from "../types";
import locale from "../locales/en";

export const operations = {
	string: {
		ct: {
			name: () => locale.contains,
			handler: (item: string, str: string) => item.toLowerCase().includes(str.toLowerCase()),
		},
		nct: {
			name: () => locale.notContains,
			handler: (item: string, str: string) => !item.toLowerCase().includes(str.toLowerCase()),
		},
		eq: { name: () => locale.equal, handler: (item: string, str: string) => item === str },
		neq: { name: () => locale.notEqual, handler: (item: string, str: string) => item !== str },
	},
	number: {
		lt: { name: () => "<", handler: (item: number, inpVal: number) => item < inpVal },
		gt: { name: () => ">", handler: (item: number, inpVal: number) => item > inpVal },
		leq: { name: () => "<=", handler: (item: number, inpVal: number) => item <= inpVal },
		geq: { name: () => ">=", handler: (item: number, inpVal: number) => item >= inpVal },
		// tslint:disable-next-line:triple-equals
		eq: { name: () => "=", handler: (item: number, inpVal: number) => item == inpVal },
		// tslint:disable-next-line:triple-equals
		neq: { name: () => "<>", handler: (item: number, inpVal: number) => item != inpVal },
	},
};

export class Filter extends View {
	config: any;
	protected _meta: IFilterMeta;
	protected _htmlEvents: any;
	protected _inputTimeout: any;
	protected _data: any[];
	protected _item: IField;
	protected _popup: Popup;

	constructor(config) {
		super(null, config);
		this._popup = new Popup();

		this.config.width = 330;
		this.config.height = 370;
		this._htmlEvents = {
			onclick: eventHandler(ev => locate(ev), {
				dhx_filter_selectAll: _ev => {
					if (this._meta.values.length) {
						this._meta.values = [];
					} else {
						this._meta.values = [...this._data];
					}
					this.paint();
				},
				dhx_button: (_ev, item) => {
					if (item === "hide") {
						this.config.events.fire("filterCancel", [this._item.id, this.getMeta()]);
						this.hide();
					}
					if (item === "apply") {
						this.config.events.fire("filterApply", [this._item.id, this.getMeta()]);
						this.hide();
					}
				},
			}),
			oninput: eventHandler(ev => locate(ev), {
				dhx_field_input: ev => {
					if (this._inputTimeout) {
						clearTimeout(this._inputTimeout);
						this._inputTimeout = null;
					}
					this._inputTimeout = setTimeout(() => {
						const val = ev.target.value.trim();
						if (this._validateInput(val)) {
							ev.target.classList.remove("dhx_alert");
							this._meta.filter = val;

							const operation = operations[this._item.type][this._meta.operation].handler;
							this._meta.values = this._data.filter(itm => operation(itm, this._meta.filter));

							this.paint();
						} else {
							ev.target.classList.add("dhx_alert");
						}
					}, 200);
				},
				dhx_filter_select: ev => {
					this._meta.operation = ev.target.value;
					const operation = operations[this._item.type][this._meta.operation].handler;
					this._meta.values = this._data.filter(itm => operation(itm, this._meta.filter));
					this.paint();
				},
			}),
			onchange: eventHandler(ev => locate(ev), {
				dhx_checkbox: ev => {
					this._checkHandler(ev.target);
					this.paint();
				},
			}),
		};
		this._popup.events.on(PopupEvents.beforeHide, isOutside => {
			if (isOutside) {
				this.config.events.fire("filterApply", [this._item.id, this.getMeta()]);
			}
		});
		const view = create({ render: () => this._getContent() });
		this.mount(this._popup, view);
	}

	setMeta(item: IField, meta: IFilterMeta, uniqueData: any[]) {
		this._item = item;
		this._data = uniqueData;

		this._setMeta(meta);
	}

	getMeta(): IFilterMeta {
		if (this._meta.values.length === this._data.length) {
			this._meta.values = [];
		}

		return this._meta;
	}

	show(node: HTMLElement) {
		this._popup.show(node, { mode: "bottom", centering: true });
	}

	hide() {
		this._popup.hide();
	}

	isVisible() {
		return this._popup.isVisible();
	}

	getItem(): IField {
		return this._item;
	}

	protected _setMeta(oldMeta: IFilterMeta) {
		this._meta = oldMeta || {};
		this._meta.operation = this._meta.operation || (this._item.type === "string" ? "ct" : "lt");
		this._meta.values = this._meta.values || [...this._data];
	}

	protected _checkHandler(checkbox: any) {
		const val =
			checkbox.value === ""
				? checkbox.value
				: isNaN(checkbox.value)
				? checkbox.value
				: parseFloat(checkbox.value);
		const index = this._meta.values.indexOf(val);
		if (index > -1 || !checkbox.checked) {
			this._meta.values.splice(index, 1);
		} else {
			this._meta.values.push(val);
		}
	}

	protected _validateInput(val: any): boolean {
		if (!val) {
			return true;
		}
		switch (this._item.type) {
			case "string":
				return true;
			case "number":
				return !isNaN(val);
			default:
				break;
		}
	}

	protected _getContent() {
		return el(
			".dhx_filter",
			{
				oninput: this._htmlEvents.oninput,
				onchange: this._htmlEvents.onchange,
				onclick: this._htmlEvents.onclick,
			},
			[
				this._getFilterInput(),
				el(".dhx_filter_content", [
					el(
						".dhx_filter_selectAll",
						this._meta.values.length ? locale.unselectAll : locale.selectAll
					),
					this._getCheckboxes(),
				]),
				el(".dhx_filter-buttons", [
					el(
						"button",
						{
							class:
								"dhx_button dhx_button--view_link dhx_button--color_primary dhx_button--size_medium",
							dhx_id: "hide",
							type: "button",
						},
						locale.cancel
					),
					el(
						"button",
						{
							class:
								"dhx_button dhx_button--view_flat dhx_button--color_primary dhx_button--size_medium",
							dhx_id: "apply",
							type: "button",
						},
						locale.apply
					),
				]),
			]
		);
	}

	protected _getOperations(type = "string"): any[] {
		const op = operations[type];
		const options = [];
		for (const key in op) {
			options.push(
				el(
					"option",
					{
						value: key,
						selected: key === this._meta.operation,
					},
					op[key].name()
				)
			);
		}
		return options;
	}

	protected _getFilterInput() {
		return el(".dhx_filter_field", [
			el(".dhx_field_select", [
				el(
					"select",
					{ class: "dhx_filter_select", _key: "sel" },
					this._getOperations(this._item.type)
				),
			]),
			el(".dhx_field_input", [
				el(
					"input.dhx_input",
					{
						class: "dhx_field_input",
						value: this._meta.filter || "",
						placeholder: locale.typeHere,
						type: "text",
						_key: "inp",
					},
					this._meta.filter || ""
				),
			]),
		]);
	}

	protected _formatLabel(label: any): any {
		return label;
	}

	protected _getCheckboxData() {
		let data = this._data;
		if (this._meta.filter) {
			const operation = operations[this._item.type][this._meta.operation].handler;
			data = data.filter(item => operation(item, this._meta.filter));
		}
		return data;
	}

	protected _getCheckboxes() {
		const data = this._getCheckboxData();
		const checkboxes = el(
			".dhx_list",
			data.map((label, i) => {
				const isChecked = this._meta.values.includes(label);
				return el(".dhx_list_item", { dhx_list_item: i }, [
					el("label.dhx_checkbox", [
						el("input.dhx_checkbox__input", {
							type: "checkbox",
							value: label,
							dhx_list_item: i,
							_key: i,
							checked: isChecked,
						}),
						el(".dhx_checkbox__visual-input"),
						el(".dhx_label", this._formatLabel(label)),
					]),
				]);
			})
		);
		return checkboxes;
	}
}
